import * as React from "react";
import { Timer } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatElapsed } from "@/lib/elapsed";

/**
 * Timer / ETA chip used in order & working views. Neutral surface with the
 * numeral set in a mono-ish tabular face.
 *
 * Two modes:
 *  - children mode: pass any label as children (+ optional `tone`).
 *  - split mode: pass `elapsed`/`target` to render `<elapsed>m / <target>m`,
 *    auto-toning the elapsed value (over target → danger). Absorbs the old
 *    cooking TimerPill. Pass `elapsedSeconds` instead of `elapsed` for a live
 *    cook: it reads `7:04 / 27m`, so a chef can see the thing moving.
 */
export interface TimeChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Optional leading icon; defaults to a timer. Pass null to omit. */
  icon?: React.ReactNode;
  tone?: "default" | "ok" | "over";
  /** Split mode: elapsed minutes (coloured). */
  elapsed?: number;
  /** Split mode: elapsed seconds, rendered `m:ss`. Wins over `elapsed`. */
  elapsedSeconds?: number;
  /** Split mode: target minutes (muted). */
  target?: number;
}

const toneClass: Record<NonNullable<TimeChipProps["tone"]>, string> = {
  default: "text-(--color-text-secondary)",
  ok: "text-(--color-success-text)",
  over: "text-(--color-danger-text)",
};

export function TimeChip({
  className,
  icon,
  tone,
  elapsed,
  elapsedSeconds,
  target,
  children,
  ...props
}: TimeChipProps) {
  const seconds = elapsedSeconds ?? (elapsed != null ? elapsed * 60 : null);
  const isSplit = seconds != null && target != null;
  const resolvedTone: NonNullable<TimeChipProps["tone"]> =
    tone ?? (isSplit && seconds > target * 60 ? "over" : isSplit ? "ok" : "default");

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-(--color-border) bg-(--color-surface-muted) px-2.5 py-1 text-sm font-semibold tabular-nums",
        !isSplit && toneClass[resolvedTone],
        className,
      )}
      {...props}
    >
      {icon === undefined ? (
        <Timer className="h-3.5 w-3.5 text-(--color-text-secondary)" />
      ) : (
        icon
      )}
      {isSplit ? (
        <span className="leading-none whitespace-nowrap">
          <span className={toneClass[resolvedTone]}>
            {elapsedSeconds != null ? formatElapsed(elapsedSeconds) : `${elapsed}m`}
          </span>
          <span className="text-(--color-text-secondary)">/{target}m</span>
        </span>
      ) : (
        children
      )}
    </span>
  );
}
